import Cookies from 'js-cookie';

import { assertNonNullish, NonNullish } from 'utils/nullish-check';

// Domains outside the Shopify redirect account (e.g. Tmall) don't share our path structure.
const NON_SHOPIFY_DOMAINS = ['rains.tmall.com'];

const EU_COUNTRIES = [
  'AT',
  'BE',
  'BG',
  'CY',
  'CZ',
  'DE',
  'DK',
  'EE',
  'ES',
  'FI',
  'FR',
  'GR',
  'GB',
  'HU',
  'HR',
  'IE',
  'IT',
  'LT',
  'LU',
  'LV',
  'MT',
  'PL',
  'PT',
  'NL',
  'RO',
  'SE',
  'SI',
  'SK',
  'CN',
  'US',
];

type Config = {
  /**
   * Whether the region will be set from the [Navigator langauge](https://developer.mozilla.org/en-US/docs/Web/API/Navigator/language).
   * If false find the region from the IP address.
   */
  readonly useRegionFromNavigator: boolean;
  /**
   * Whether the sites will be set from the `hreflang` elements in the head.
   * If false, you'll have to set the sites manually.
   */
  readonly setSitesFromHreflangs: boolean;
  /** Whether to redirect to the selected site if the user has already selected a site. Only works if the user has selected a site. */
  readonly redirectIfHasSelected: boolean;
  readonly regions?: Array<Region> | undefined;
};

type Region = {
  /** ISO 3166-1 alpha-2 or "default" */
  region: string;
  locales: Locale[];
};

type Locale = {
  /** ISO 3166-1 alpha-2 */
  locale: string;
  url: string;
  isCurrent: boolean;
};

type RegionFilter = Partial<{
  region: string;
  locale: string;
}>;

export type Selection = {
  region: Region;
  locale: Locale;
};

const STORAGE_KEY = 'theme:selected-region';

export default class ShopRegions {
  private readonly config: Config;
  readonly regions: Array<Region> = [];

  constructor(config: Config) {
    this.config = config;

    if (this.config.regions) {
      this.regions.push(...this.config.regions);
    }

    if (config.setSitesFromHreflangs) {
      this.regions.push(...this.getRegionsFromHreflangs());
    }

    const url = new URL(window.location.href);
    if (url.searchParams.get('redirect')) {
      this.saveToStorage(this.getCurrentLocale());
    }

    this.getRankedRegions = this.getRankedRegions.bind(this);
    this.getFilteredRegions = this.getFilteredRegions.bind(this);
    this.getIntendedSite = this.getIntendedSite.bind(this);
    this.getCurrentLocale = this.getCurrentLocale.bind(this);
  }

  private loadDependecies() {
    return new Promise<void>((resolve, reject) => {
      window.Shopify.loadFeatures(
        [
          {
            name: 'consent-tracking-api',
            version: '0.1',
          },
        ],
        (error) => {
          if (error) {
            reject();
          } else {
            resolve();
          }
        },
      );
    });
  }

  private getRegionsFromHreflangs(): Region[] {
    const links = Array.from(
      document.querySelectorAll<HTMLLinkElement>('link[rel="alternate"][hreflang]'),
    );

    const map = new Map<string, Locale[]>();

    for (const link of links) {
      let [locale, region] = link.hreflang.split('-');

      assertNonNullish(locale);

      if (locale === 'x') {
        locale = theme.primaryLocale;
      }

      if (!region) {
        region = 'default';
      }

      const locales = map.get(region) ?? [];
      if (locales.some((l) => l.locale === locale)) continue;

      const isCurrent =
        locale === Shopify.locale.split('-').at(0) && region === Shopify.country.split('-').at(0);
      locales.push({ locale, url: link.href, isCurrent });
      map.set(region, locales);
    }

    return Array.from(map.entries()).map(([region, locales]) => ({ region, locales }));
  }

  private async getRegion() {
    await this.loadDependecies();

    let region = Cookies.get(STORAGE_KEY)?.split('-').at(1);

    if (!region && this.config.useRegionFromNavigator) {
      region = navigator.language.split('-').at(1);
    }

    if (!region) {
      region = Shopify.customerPrivacy?.getRegion().substring(0, 2) || undefined;
    }

    return region;
  }

  private getLocale() {
    return Cookies.get(STORAGE_KEY)?.split('-').at(0) ?? navigator.language.split('-').at(0);
  }

  private saveToStorage({ locale, region }: Selection) {
    Cookies.set(STORAGE_KEY, `${locale.locale}-${region.region}`, { expires: 365 });
  }

  hasSelectedSite() {
    return Cookies.get(STORAGE_KEY) !== undefined;
  }

  getRankedRegions({ region, locale }: RegionFilter) {
    return [...this.regions]
      .sort((a, b) => Number(b.region === 'default') - Number(a.region === 'default'))
      .sort((a, b) => {
        if (locale) {
          return (
            Number(b.locales.some((l) => l.locale === locale)) -
            Number(a.locales.some((l) => l.locale === locale))
          );
        }

        return 0;
      })
      .sort((a, b) => {
        if (region) {
          return Number(b.region === region) - Number(a.region === region);
        }

        return 0;
      });
  }

  async getUserRankedRegions() {
    const locale = this.getLocale();
    const region = await this.getRegion();

    return this.getRankedRegions({ region, locale });
  }

  getFilteredRegions({ region, locale }: RegionFilter, shouldRank = false) {
    return (shouldRank ? this.getRankedRegions({ region, locale }) : this.regions).filter((r) => {
      if (region && r.region !== region) {
        return false;
      }

      if (locale && !r.locales.some((l) => l.locale === locale)) {
        return false;
      }

      return true;
    });
  }

  getCurrentLocale(): Selection {
    for (const region of this.regions) {
      const locale = region.locales.find((l) => l.isCurrent);
      if (locale) return { region, locale };
    }

    const region = NonNullish(this.regions.find((r) => r.region === 'default'));
    return { region, locale: NonNullish(region.locales.at(0)) };
  }

  selectSite({ region, locale }: Selection) {
    this.saveToStorage({ region, locale });

    const current = this.getCurrentLocale();
    if (locale !== current.locale) {
      const url = new URL(locale.url);
      url.search = window.location.search;
      url.hash = window.location.hash;

      if (NON_SHOPIFY_DOMAINS.some((domain) => url.hostname === domain)) {
        url.pathname = '/';
      } else if (theme.pageType !== '404') {
        const currentPath = window.location.pathname
          .replace(theme.routes.root, '/')
          .replace('//', '/');

        if (!url.pathname.endsWith(currentPath) && !currentPath.endsWith(url.pathname)) {
          url.pathname = `${url.pathname}${currentPath}`.replace('//', '/');
        }
      }

      url.searchParams.set('redirect', '1');

      window.location.replace(url);
    }
  }

  async doAutomaticRedirect() {
    const site = await this.getIntendedSite();

    this.selectSite(site);
  }

  async shouldRedirect() {
    if (
      EU_COUNTRIES.includes(await this.getRegion()) &&
      (!this.config.redirectIfHasSelected || !this.hasSelectedSite())
    ) {
      return false;
    }

    return this.getCurrentLocale().locale !== (await this.getIntendedSite()).locale;
  }

  async shouldShowPrompt() {
    return !(await this.shouldRedirect());
  }

  async getIntendedSite(): Promise<Selection> {
    const localeStr = this.getLocale();
    const regionStr = await this.getRegion();

    const region = NonNullish(
      this.getRankedRegions({ region: regionStr, locale: localeStr }).at(0),
    );
    const locale =
      region.locales.find((l) => l.locale === localeStr) ?? NonNullish(region.locales[0]);

    return { region, locale };
  }
}
